import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { correctionalFacilityId, geoData } from '@fs/zion-config'
import { useCanAddPersonalData } from '@fs/zion-permissions'

import { useFeatureFlag } from '@fs/zion-flags'
import { useUser } from '@fs/zion-user'
import { splunk } from '@fs/zion-splunk-logger'
import { usePrisonReportsPermission } from '../hooks/usePermission'
import uuid from '../utils/uuid'

interface AppContextType {
  accessFromPrison: boolean
  canAddPersonalData: boolean | undefined
  clientId: string
  correctionalFacilityId: string | undefined
  geoCountry: string | undefined
  isFullscreen: boolean
  prisonReportsPermission: { hasPermission: boolean; loading: boolean }
  setFullscreen: React.Dispatch<React.SetStateAction<boolean>>
  setShareablePage: React.Dispatch<React.SetStateAction<boolean>>
  setSkipLinkId: React.Dispatch<React.SetStateAction<string | null>>
  signedIn: boolean | undefined
  shareablePage: boolean
  skipLinkId: string | null
  userLoading: boolean | undefined
}

export const AppContext = createContext<AppContextType>({} as AppContextType)
const key = 'give-clientId'

export const AppProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const [shareablePage, setShareablePage] = useState(false)
  const [skipLinkId, setSkipLinkId] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [canAddPersonalData] = useCanAddPersonalData()
  const { hasPermission, loading } = usePrisonReportsPermission()
  const clientId = window.localStorage.getItem(key) || uuid()
  const accessFromPrison = !!correctionalFacilityId && typeof correctionalFacilityId === 'string'
  const reportAccessFlag = useFeatureFlag('give_corrections_report_access')
  const prisonReportsPermissionOverrideFlag = useFeatureFlag('give_prison_reports_access_override')
  const prisonLoggingFlag = useFeatureFlag('give_corrections_logging')
  const { signedIn, userLoading } = useUser()

  const prisonReportsPermission = prisonReportsPermissionOverrideFlag.isOn || (hasPermission && reportAccessFlag.isOn)

  useEffect(() => {
    window.localStorage.setItem(key, clientId)
  }, [clientId])

  const value = useMemo(
    () => ({
      accessFromPrison,
      canAddPersonalData,
      clientId,
      correctionalFacilityId,
      geoCountry: geoData?.country,
      isFullscreen,
      prisonReportsPermission: {
        hasPermission: prisonReportsPermission,
        loading,
      },
      setFullscreen: setIsFullscreen,
      setShareablePage,
      setSkipLinkId,
      signedIn,
      shareablePage,
      skipLinkId,
      userLoading,
    }),
    [
      accessFromPrison,
      canAddPersonalData,
      clientId,
      isFullscreen,
      loading,
      prisonReportsPermission,
      shareablePage,
      signedIn,
      skipLinkId,
      userLoading,
    ]
  )

  const loggingConfig = prisonLoggingFlag.config as { correctionalFacilityIdLogging?: boolean } | undefined

  useEffect(() => {
    if (prisonLoggingFlag.isOn && loggingConfig?.correctionalFacilityIdLogging) {
      splunk.log({
        message: 'app-provider:correctional-facility-id',
        correctionalFacilityId,
        accessFromPrison,
      })
    }
  }, [prisonLoggingFlag.isOn, loggingConfig?.correctionalFacilityIdLogging, accessFromPrison])

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export const useAppProvider = (): AppContextType => {
  return useContext(AppContext)
}
